import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { 
  ChevronLeft, 
  MapPin, 
  Calendar, 
  BookOpen, 
  MessageSquare,
  Users,
  ExternalLink
} from 'lucide-react';
import { eventApi } from '../services/api';
import { 
  PageLoading, 
  GlassCard,
  CertaintyBadge,
  ContentBadge,
  SourceCitation,
  ArabicVerse
} from '../components/shared';

const EventDetail = () => {
  const { t } = useTranslation();
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await eventApi.getEvent(eventId);
        setEvent(response.data);
      } catch (error) {
        console.error('Error fetching event:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId]);

  if (loading) return <PageLoading />;
  if (!event) return <div className="text-center py-20 text-text-muted">{t('timeline.not_found')}</div>;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div 
        className="relative py-20 px-4 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1683138155815-d7edd806d8a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxkZXNlcnQlMjBuaWdodCUyMHNreSUyMHN0YXJzfGVufDB8fHx8MTc3NTQ2OTA2MXww&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/80 to-background" />
        <div className="relative z-10 max-w-4xl mx-auto">
          <Link to="/prophet-timeline" className="inline-flex items-center text-text-muted hover:text-primary mb-6 transition-colors">
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t('timeline.back_to_timeline')}
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <ContentBadge type="historical" />
            <CertaintyBadge level={event.certainty_level} />
          </div>
          
          <h1 className="font-arabic text-3xl sm:text-4xl text-primary mb-4" dir="rtl">
            {event.title_arabic}
          </h1>
          <h2 className="text-2xl sm:text-3xl font-serif text-text-primary mb-2">
            {event.title_french || event.title_english}
          </h2>
          <p className="text-lg text-text-secondary">{event.title_english}</p>
          
          <div className="flex flex-wrap gap-4 mt-6 text-sm text-text-muted">
            {event.date_hijri && (
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {event.date_hijri}
              </span>
            )}
            {event.date_gregorian && (
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {event.date_gregorian}
              </span>
            )}
            {event.location && (
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {event.location}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main narrative */}
          <div className="lg:col-span-2 space-y-8">
            <GlassCard>
              <h3 className="font-serif text-xl text-text-primary mb-4">{t('timeline.narrative')}</h3>
              <p className="text-text-secondary leading-relaxed whitespace-pre-line">
                {event.narrative}
              </p>
              
              <SourceCitation sources={event.sources} />
            </GlassCard>

            {/* Related Verses */}
            {event.verses_data && event.verses_data.length > 0 && (
              <GlassCard>
                <h3 className="font-serif text-xl text-text-primary mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  {t('timeline.related_verses')}
                </h3>
                <div className="space-y-4">
                  {event.verses_data.map((verse, i) => (
                    <div key={i} className="p-4 bg-surface-elevated rounded-lg">
                      <ContentBadge type="quran" className="mb-2" />
                      <p className="font-arabic text-xl text-white text-right mb-2" dir="rtl">
                        {verse.arabic_text}
                      </p>
                      <p className="text-sm text-text-secondary">{verse.translation_french || verse.translation_english}</p>
                      <Link 
                        to={`/quran/${verse.surah_number}`}
                        className="text-xs text-primary hover:underline mt-2 inline-block"
                      >
                        {t('timeline.see_surah', { surah: verse.surah_number, verse: verse.verse_number })}
                      </Link>
                    </div>
                  ))}
                </div>
              </GlassCard>
            )}

            {/* Related Hadiths */}
            {event.hadiths_data && event.hadiths_data.length > 0 && (
              <GlassCard>
                <h3 className="font-serif text-xl text-text-primary mb-4 flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  Hadiths associés
                </h3>
                <div className="space-y-4">
                  {event.hadiths_data.map((hadith, i) => (
                    <div key={i} className="p-4 bg-surface-elevated rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <ContentBadge type="hadith" />
                        <span className="text-xs px-2 py-0.5 rounded bg-emerald-900/30 text-emerald-400">
                          {hadith.grade}
                        </span>
                      </div>
                      <p className="text-text-secondary">{hadith.translation_french || hadith.translation_english}</p>
                      {hadith.narrator && (
                        <p className="text-xs text-text-muted mt-2">{t('hadith.narrated_by', { name: hadith.narrator })}</p>
                      )}
                    </div>
                  ))}
                </div>
              </GlassCard>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Period */}
            <GlassCard>
              <h4 className="text-sm font-medium text-text-muted mb-2">{t('timeline.period')}</h4>
              <span className="px-3 py-1 rounded-full bg-primary/20 text-primary text-sm capitalize">
                {event.period?.replace('_', ' ')}
              </span>
            </GlassCard>

            {/* Related Companions */}
            {event.companions_data && event.companions_data.length > 0 && (
              <GlassCard>
                <h4 className="font-serif text-lg text-text-primary mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  {t('timeline.involved_companions')}
                </h4>
                <div className="space-y-3">
                  {event.companions_data.map((companion, i) => (
                    <Link 
                      key={i}
                      to={`/companions/${companion.id}`}
                      className="block p-3 bg-surface-elevated rounded-lg hover:border-primary/30 border border-transparent transition-all"
                    >
                      <p className="text-text-primary">{companion.name_french || companion.name_english}</p>
                      <p className="font-arabic text-sm text-primary" dir="rtl">{companion.name_arabic}</p>
                    </Link>
                  ))}
                </div>
              </GlassCard>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetail;
